<template>
    <div class="fac-chart">
        <div class="fac-chart__header">
            <div>
                <p class="fac-chart__eyebrow">Landscape inventory</p>
                <h3 class="fac-chart__title">Palm & Trees by type</h3>
            </div>
            <div v-if="total > 0" class="fac-chart__total">
                <span class="fac-chart__total-label">Total plants</span>
                <span class="fac-chart__total-value">{{ total }}</span>
            </div>
        </div>
        <div v-if="!hasData" class="fac-chart__empty">No palm & trees data available yet.</div>
        <apexchart
            v-else
            type="donut"
            height="300"
            :options="chartOptions"
            :series="series"
            @dataPointSelection="onSliceSelect"
            @legendClick="onLegendClick"
        />
    </div>
</template>

<script>
export default {
    name: "FacilityPalmTreesChart",
    props: {
        data: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    emits: ["typeSelect"],
    data() {
        return {
            series: [],
            activeRows: [],
            chartOptions: {
                chart: {
                    type: "donut",
                    fontFamily: "inherit",
                    toolbar: { show: false },
                    animations: { enabled: true, speed: 450 },
                },
                labels: [],
                colors: ["#ca8a04", "#0f766e"],
                legend: {
                    position: "bottom",
                    fontSize: "12px",
                    markers: { radius: 10 },
                    itemMargin: { horizontal: 8, vertical: 4 },
                    formatter: function (val, opts) {
                        return (
                            val +
                            " · " +
                            opts.w.globals.series[opts.seriesIndex]
                        );
                    },
                },
                dataLabels: {
                    enabled: true,
                    style: { fontSize: "11px", fontWeight: 600 },
                    dropShadow: { enabled: false },
                    formatter: function (val) {
                        return Math.round(val) + "%";
                    },
                },
                stroke: { width: 2, colors: ["#ffffff"] },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "66%",
                            labels: {
                                show: true,
                                name: {
                                    show: true,
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#78716c",
                                },
                                value: {
                                    show: true,
                                    fontSize: "20px",
                                    fontWeight: 800,
                                    color: "#1c1917",
                                },
                                total: {
                                    show: true,
                                    label: "Plants",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#78716c",
                                    formatter: function (w) {
                                        return w.globals.seriesTotals.reduce(
                                            function (a, b) {
                                                return a + b;
                                            },
                                            0
                                        );
                                    },
                                },
                            },
                        },
                    },
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " plants";
                        },
                    },
                },
            },
        };
    },
    computed: {
        hasData() {
            return (
                Array.isArray(this.data) &&
                this.data.some(function (item) {
                    return item && item.value > 0;
                })
            );
        },
        total() {
            if (!Array.isArray(this.data)) return 0;
            return this.data.reduce(function (sum, item) {
                return sum + (item && item.value ? item.value : 0);
            }, 0);
        },
    },
    watch: {
        data: {
            handler: function (rows) {
                if (!Array.isArray(rows)) return;
                var ordered = this.normalizeRows(rows);
                this.activeRows = ordered;
                this.series = ordered.map(function (item) {
                    return item.value;
                });
                this.chartOptions = Object.assign({}, this.chartOptions, {
                    labels: ordered.map(function (item) {
                        return item.label;
                    }),
                    colors: ordered.map(function (item) {
                        return item.label === "Palm" ? "#ca8a04" : "#0f766e";
                    }),
                });
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        normalizeRows(rows) {
            var preferred = ["Palm", "Trees"];
            var map = {};
            for (var i = 0; i < rows.length; i++) {
                if (!rows[i] || !rows[i].label) continue;
                map[rows[i].label] = rows[i].value || 0;
            }

            var result = [];
            for (var p = 0; p < preferred.length; p++) {
                var label = preferred[p];
                if (map[label] !== undefined) {
                    result.push({ label: label, value: map[label] });
                    delete map[label];
                }
            }

            var extras = Object.keys(map).sort();
            for (var e = 0; e < extras.length; e++) {
                result.push({ label: extras[e], value: map[extras[e]] });
            }

            return result.filter(function (item) {
                return item.value > 0;
            });
        },
        emitTypeSelect(index) {
            if (typeof index !== "number" || index < 0) return;

            var label = null;
            var value = 0;
            if (this.activeRows[index] && this.activeRows[index].label) {
                label = this.activeRows[index].label;
                value = this.activeRows[index].value || 0;
            } else if (
                this.chartOptions.labels &&
                this.chartOptions.labels[index]
            ) {
                label = this.chartOptions.labels[index];
            }

            if (!label) return;

            this.$emit("typeSelect", {
                plantType: label,
                value: value,
            });
        },
        onSliceSelect(event, chartContext, config) {
            var index = -1;
            var source = config || chartContext || event;
            if (source && typeof source.dataPointIndex === "number" && source.dataPointIndex >= 0) {
                index = source.dataPointIndex;
            } else if (source && typeof source.seriesIndex === "number" && source.seriesIndex >= 0) {
                index = source.seriesIndex;
            }
            this.emitTypeSelect(index);
        },
        onLegendClick(chartContext, seriesIndex) {
            var index = seriesIndex;
            if (typeof index !== "number") {
                if (chartContext && typeof chartContext.seriesIndex === "number") {
                    index = chartContext.seriesIndex;
                } else {
                    index = -1;
                }
            }
            this.emitTypeSelect(index);
        },
    },
};
</script>

<style lang="scss" scoped>
.fac-chart {
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 1rem;
    padding: 1.25rem 1.35rem 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    height: 100%;
}

.fac-chart__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.35rem;
}

.fac-chart__eyebrow {
    margin: 0;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
}

.fac-chart__title {
    margin: 0.2rem 0 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1c1917;
}

.fac-chart__total {
    text-align: right;
    flex-shrink: 0;
}

.fac-chart__total-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #a8a29e;
}

.fac-chart__total-value {
    display: block;
    margin-top: 0.15rem;
    font-size: 1.25rem;
    font-weight: 800;
    color: #0f766e;
    line-height: 1;
}

.fac-chart__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 16rem;
    color: #a8a29e;
    font-size: 0.9rem;
}

.fac-chart :deep(.apexcharts-pie-area),
.fac-chart :deep(.apexcharts-legend-series) {
    cursor: pointer;
}
</style>
